import type {BadgeProps, MantineColor} from '@mantine/core';
import {
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Menu,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {IconChevronDown, IconSearch} from '@tabler/icons-react';
import React from 'react';
import type {ActionVariant} from 'src/design/atoms/actionConfig';
import {getActionConfig} from 'src/design/atoms/actionConfig';
import {Button as DefaultButton} from 'src/design/atoms/Button';
import {IconSize} from 'src/design/atoms/icons/CommonIcons';

/**
 * PageHeader - A compositional header component for pages.
 * @registry Title/logo/search/badges/meta/actions header for list & detail pages.
 *
 * Ported from `library-m/layout/PageHeader`. Prop surface and exported
 * names (compound `PageHeader` plus all sub-components/prop types) kept
 * identical; only the `actionConfig` import was swapped to the
 * `design/atoms` port.
 *
 * Works for both list pages and detail pages. Badges and Meta share one
 * wrapping row under the title:
 *
 * List page example:
 * ```tsx
 * <PageHeader>
 *   <PageHeader.Title icon={<ServerIcon />} subtitle="Manage workspaces">
 *     Instances
 *   </PageHeader.Title>
 *   <PageHeader.Search value={search} onChange={setSearch} />
 *   <PageHeader.Actions>
 *     <PageHeader.Action variant="create" onClick={handleCreate}>
 *       New Instance
 *     </PageHeader.Action>
 *     <PageHeader.Action variant="refresh" onClick={refetch} loading={isFetching} />
 *   </PageHeader.Actions>
 * </PageHeader>
 * ```
 *
 * Detail page example:
 * ```tsx
 * <PageHeader>
 *   <PageHeader.Title icon={<IconTask />}>Task Name</PageHeader.Title>
 *   <PageHeader.Badges>
 *     <PageHeader.Badge color="green">Published</PageHeader.Badge>
 *   </PageHeader.Badges>
 *   <PageHeader.Meta>TSK-12 - Created 5d ago</PageHeader.Meta>
 *   <PageHeader.Actions>
 *     <PageHeader.Action variant="edit" onClick={handleEdit}>Edit</PageHeader.Action>
 *     <PageHeader.Action variant="save" loading={saving}>Save</PageHeader.Action>
 *   </PageHeader.Actions>
 * </PageHeader>
 * ```
 */

// --- Sub-component Props ---

export interface PageHeaderProps {
  children: React.ReactNode;
}

export interface PageHeaderTitleProps {
  children: React.ReactNode;
  icon?: React.ReactNode;
  subtitle?: string;
}

export interface PageHeaderSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  width?: number | string;
}

export interface PageHeaderBadgesProps {
  children: React.ReactNode;
}

export interface PageHeaderBadgeProps {
  children: React.ReactNode;
  color: BadgeProps['color'];
  size?: BadgeProps['size'];
  variant?: BadgeProps['variant'];
}

export interface PageHeaderLogoProps {
  src: string;
  alt?: string;
  maxHeight?: number;
  maxWidth?: number;
}

export interface PageHeaderMetaProps {
  children: React.ReactNode;
}

export interface PageHeaderMetaItemProps {
  label: string;
  children: React.ReactNode;
}

export interface PageHeaderActionsProps {
  children: React.ReactNode;
}

export type PageHeaderActionVariant = ActionVariant;

export interface PageHeaderActionProps {
  /** Button label - optional for icon-only buttons */
  children?: React.ReactNode;
  /** Custom icon (overrides variant icon) */
  icon?: React.ReactNode;
  onClick?: () => void;
  /** Visual variant - semantic variants include built-in icons */
  variant?: PageHeaderActionVariant;
  loading?: boolean;
  disabled?: boolean;
  /**
   * Render as a polymorphic element instead of a `<button>` — e.g. pass
   * `component={Link} to="/path"` for a navigation action. Keeps this
   * organism free of a direct `react-router-dom` import while still
   * rendering a real link element (not a button with a `navigate()`
   * `onClick`).
   */
  component?: React.ElementType;
  /** Destination when `component` is a routing `Link` (e.g. `to` for react-router's `Link`). */
  to?: string;
}

export interface PageHeaderActionMenuProps {
  items: {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    color?: MantineColor;
    disabled?: boolean;
  }[];
  label?: string;
}

// --- Sub-components ---

const PageHeaderTitle: React.FC<PageHeaderTitleProps> = ({
  children,
  icon,
  subtitle,
}) => (
  <Box>
    <Group gap="xs" align="center" wrap="nowrap">
      {icon && (
        <ThemeIcon
          size="md"
          variant="light"
          color="gray"
          style={{flexShrink: 0}}>
          {icon}
        </ThemeIcon>
      )}
      <Title order={2} fz={{base: 'h3', md: 'h2'}} style={{minWidth: 0}}>
        {children}
      </Title>
    </Group>
    {subtitle && (
      <Text size="sm" c="dimmed" mt={4}>
        {subtitle}
      </Text>
    )}
  </Box>
);
PageHeaderTitle.displayName = 'PageHeader.Title';

const PageHeaderSearch: React.FC<PageHeaderSearchProps> = ({
  value,
  onChange,
  placeholder = 'Search...',
  width,
}) => (
  <TextInput
    placeholder={placeholder}
    leftSection={<IconSearch size={IconSize.md} />}
    value={value}
    onChange={e => onChange(e.target.value)}
    w={width ?? {base: '100%', sm: 220}}
    size="sm"
  />
);
PageHeaderSearch.displayName = 'PageHeader.Search';

const PageHeaderLogo: React.FC<PageHeaderLogoProps> = ({
  src,
  alt = '',
  maxHeight = 40,
  maxWidth = 130,
}) => (
  <img
    src={src}
    alt={alt}
    style={{maxHeight, maxWidth, objectFit: 'contain'}}
  />
);
PageHeaderLogo.displayName = 'PageHeader.Logo';

const PageHeaderBadges: React.FC<PageHeaderBadgesProps> = ({children}) => (
  <Group gap="xs">{children}</Group>
);
PageHeaderBadges.displayName = 'PageHeader.Badges';

const PageHeaderBadge: React.FC<PageHeaderBadgeProps> = ({
  children,
  color,
  size = 'md',
  variant = 'filled',
}) => (
  <Badge size={size} color={color} variant={variant}>
    {children}
  </Badge>
);
PageHeaderBadge.displayName = 'PageHeader.Badge';

const PageHeaderMeta: React.FC<PageHeaderMetaProps> = ({children}) => (
  <Group gap="md" c="dimmed" fz="sm">
    {children}
  </Group>
);
PageHeaderMeta.displayName = 'PageHeader.Meta';

const PageHeaderMetaItem: React.FC<PageHeaderMetaItemProps> = ({
  label,
  children,
}) => (
  <Text size="sm" c="dimmed">
    <Text span fw={500} c="dimmed">
      {label}:
    </Text>{' '}
    {children}
  </Text>
);
PageHeaderMetaItem.displayName = 'PageHeader.MetaItem';

const PageHeaderActions: React.FC<PageHeaderActionsProps> = ({children}) => (
  <Group gap="sm">{children}</Group>
);
PageHeaderActions.displayName = 'PageHeader.Actions';

/** Standardized action button for PageHeader */
const PageHeaderAction: React.FC<PageHeaderActionProps> = ({
  children,
  icon,
  onClick,
  variant = 'primary',
  loading = false,
  disabled = false,
  component,
  to,
}) => {
  const config = getActionConfig(variant);
  const displayIcon = icon ?? config.icon;
  // Mantine's polymorphic `component` prop resolves to a huge overload
  // union keyed on the literal component type; a generic `React.ElementType`
  // (needed so this organism never imports `react-router-dom` directly)
  // doesn't satisfy any single member of that union. Cast the passthrough
  // props rather than the whole component.
  const polymorphicProps = {component, to} as Record<string, unknown>;

  return (
    <Button
      {...polymorphicProps}
      size="sm"
      leftSection={displayIcon}
      onClick={onClick}
      loading={loading}
      disabled={disabled}
      variant={config.buttonVariant}
      color={config.color}>
      {children ?? config.text}
    </Button>
  );
};
PageHeaderAction.displayName = 'PageHeader.Action';

const PageHeaderActionMenu: React.FC<PageHeaderActionMenuProps> = ({
  items,
  label = 'More',
}) => {
  if (items.length === 0) return null;

  return (
    <Menu shadow="md" width={200} position="bottom-end">
      <Menu.Target>
        <DefaultButton
          variant="subtle"
          color="gray"
          rightSection={<IconChevronDown size={IconSize.sm} />}>
          {label}
        </DefaultButton>
      </Menu.Target>
      <Menu.Dropdown>
        {items.map((item, index) => (
          <Menu.Item
            key={index}
            leftSection={item.icon}
            onClick={item.onClick}
            color={item.color}
            disabled={item.disabled}>
            {item.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
};
PageHeaderActionMenu.displayName = 'PageHeader.ActionMenu';

// --- Main Component ---

const PageHeaderRoot: React.FC<PageHeaderProps> = ({children}) => {
  const childArray = React.Children.toArray(children);

  // Use displayName for reliable component identification (works in production builds)
  const getDisplayName = (child: React.ReactNode): string | undefined => {
    if (React.isValidElement(child) && typeof child.type !== 'string') {
      return (child.type as React.FC & {displayName?: string}).displayName;
    }
    return undefined;
  };

  const logoChild = childArray.find(
    child => getDisplayName(child) === 'PageHeader.Logo'
  );
  const titleChild = childArray.find(
    child => getDisplayName(child) === 'PageHeader.Title'
  );
  const searchChild = childArray.find(
    child => getDisplayName(child) === 'PageHeader.Search'
  );
  const badgesChild = childArray.find(
    child => getDisplayName(child) === 'PageHeader.Badges'
  );
  const metaChild = childArray.find(
    child => getDisplayName(child) === 'PageHeader.Meta'
  );
  const actionsChild = childArray.find(
    child => getDisplayName(child) === 'PageHeader.Actions'
  );

  return (
    <Flex
      justify="space-between"
      align="flex-start"
      wrap="wrap"
      gap="md"
      mb="sm">
      <Box style={{flex: 1, minWidth: 200}}>
        {logoChild ? (
          <Group gap="xs" align="center" wrap="nowrap">
            {logoChild}
            <Box>{titleChild}</Box>
          </Group>
        ) : (
          titleChild
        )}
        {(badgesChild || metaChild) && (
          <Group gap="md" mt="xs" align="center">
            {badgesChild}
            {metaChild}
          </Group>
        )}
      </Box>
      <Group gap="sm" w={{base: '100%', sm: 'auto'}}>
        {searchChild}
        {actionsChild}
      </Group>
    </Flex>
  );
};
PageHeaderRoot.displayName = 'PageHeader';

// --- Compound Component Export ---

export const PageHeader = Object.assign(PageHeaderRoot, {
  Title: PageHeaderTitle,
  Logo: PageHeaderLogo,
  Search: PageHeaderSearch,
  Badges: PageHeaderBadges,
  Badge: PageHeaderBadge,
  Meta: PageHeaderMeta,
  MetaItem: PageHeaderMetaItem,
  Actions: PageHeaderActions,
  Action: PageHeaderAction,
  ActionMenu: PageHeaderActionMenu,
});

// Also export individual components for flexibility
export {
  PageHeaderAction,
  PageHeaderActionMenu,
  PageHeaderActions,
  PageHeaderBadge,
  PageHeaderBadges,
  PageHeaderLogo,
  PageHeaderMeta,
  PageHeaderMetaItem,
  PageHeaderSearch,
  PageHeaderTitle,
};
